/**
 * PracticeLayout - root shell for the clinical workspace.
 *
 * Layout:   [Floating pill nav] / [Side rail | Tab Bar + Page Content panel] on a cream canvas
 *
 * Fixes applied:
 *  - Export both default + named so App.tsx import works
 *  - <Outlet> instead of {children} (React Router layout route)
 *  - useAuth() from the correct hook
 *  - Removed unused imports (useEffect, Menu, CreditCard)
 *  - DropdownMenuTrigger uses a real clickable div (no asChild on native element)
 *  - TabsProvider + OpenEMR-style tab bar below the topbar
 */
import { useEffect, useMemo, useState, useRef, type ElementType, type ReactNode } from "react";
import { Outlet, Link, useLocation, useNavigate } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import {
  Calendar,
  Search,
  LogOut,
  CircleHelp,
  Rows3,
  Sparkles,
  Menu,
  Pill,
  UserRoundSearch,
  FileText,
  ClipboardList,
  History,
  UserPlus,
  Plus,
} from "lucide-react";
import { cn } from "@/lib/utils";
import type { Appointment, Patient } from "@/types";
import { Button } from "@/components/ui/button";
import { useAuth } from "@/hooks/use-auth";
import { usePermissions } from "@/hooks/use-permissions";
import { getNavSections, withProgramSections } from "@/components/layout/nav/practice-nav";
import { usePrpMode } from "@/hooks/use-prp-mode";
import PrpWorkspaceLayout from "@/pages/prp/PrpWorkspaceLayout";
import { isPrpAssigned } from "@/lib/prp-home";

import { useDebounce } from "@/hooks/use-debounce";
import { useSearchPatients } from "@/hooks/use-patients";
import { usePatientAnnouncementSummaries } from "@/hooks/use-patient-announcements";
import { PatientAnnouncementIndicator } from "@/components/patients/patient-announcement-indicator";
import { PatientBookingAlertDialog } from "@/components/patients/patient-booking-alert-dialog";
import { listAppointments } from "@/api/appointment.api";
import { useRecordExchangePendingCount } from "@/hooks/use-record-exchange";
import { useUnreadCount } from "@/hooks/use-messages";
import { usePendingBookingRequestsCount } from "@/hooks/use-booking-requests";
import { motion, AnimatePresence } from "framer-motion";
import { ThemePicker } from "@/components/shared/theme-picker";
import {
  WorkbenchFloatingDock,
  useWorkbench,
  WorkbenchTabBarSlot,
} from "@/components/layout/workbench";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { BrandLogotype, UserMenu, NotificationBell } from "@/components/layout/shell";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import type { PracticeNavItem } from "@/components/layout/nav/practice-nav";
import { isHiddenHref } from "@/lib/hidden-features";

import {
  readRecentSearches,
  writeRecentSearches,
  type StoredRecentSearch,
} from "@/lib/recent-searches";

interface SearchTarget {
  id: string;
  label: string;
  subtitle: string;
  href: string;
  icon: ElementType;
  keywords: string[];
}

interface SearchResultItem {
  id: string;
  label: string;
  subtitle: string;
  href: string;
  icon: ElementType;
  patientId?: string;
}

const ALL_SEARCH_TARGETS: SearchTarget[] = [
  {
    id: "search-patients",
    label: "Search Patients",
    subtitle: "Find charts by patient name or MRN",
    href: "/patients",
    icon: UserRoundSearch,
    keywords: ["patient", "chart", "mrn", "demographics"],
  },
  {
    id: "search-schedule",
    label: "Open Schedule",
    subtitle: "View calendar and appointment slots",
    href: "/schedule",
    icon: Calendar,
    keywords: ["schedule", "calendar", "appointment", "book"],
  },
  {
    id: "search-prescriptions",
    label: "Prescriptions",
    subtitle: "Medication workflows and refill queue",
    href: "/prescriptions",
    icon: Pill,
    keywords: ["rx", "medication", "prescription", "refill"],
  },
  {
    id: "search-billing",
    label: "Billing",
    subtitle: "Bills, payments and receipts",
    href: "/billing/bills",
    icon: ClipboardList,
    keywords: ["billing", "claims", "invoice", "payment"],
  },
  {
    id: "search-forms",
    label: "Forms & Intake",
    subtitle: "Send forms and manage the intake queue",
    href: "/forms",
    icon: ClipboardList,
    keywords: ["forms", "intake", "questionnaire", "consent", "screening"],
  },
  {
    id: "search-help",
    label: "Help centre",
    subtitle: "Guides, answers and contact",
    href: "/help",
    icon: CircleHelp,
    keywords: ["help", "support", "faq", "how", "guide"],
  },
  {
    id: "search-reports",
    label: "Analytics",
    subtitle: "Utilization, quality and performance insights",
    href: "/reports",
    icon: FileText,
    keywords: ["report", "analytics", "insights", "quality"],
  },
];

// Billing / prescriptions are hidden for Orange (see lib/hidden-features).
const SEARCH_TARGETS = ALL_SEARCH_TARGETS.filter((target) => !isHiddenHref(target.href));

// ── Floating header ───────────────────────────────────────────────────────────

/** Items shown directly in the nav pill, in this order; everything else goes under "More". */
const PRIMARY_NAV_HREFS = ["/dashboard", "/my-day", "/schedule", "/patients", "/billing/bills", "/messages", "/tasks"];
const MAX_PRIMARY_ITEMS = 6;

const PILL_CLASS = "flex h-12 shrink-0 items-center rounded-full bg-card shadow-[0_1px_2px_rgba(0,58,67,0.05)]";

function isNavActive(pathname: string, href: string, allHrefs: string[]): boolean {
  if (pathname === href) return true;
  if (href === "/dashboard" || !pathname.startsWith(href + "/")) return false;
  // A longer sibling (e.g. /schedule/booking-requests) owns its own subtree.
  return !allHrefs.some(
    (other) => other !== href && other.startsWith(href) && (pathname === other || pathname.startsWith(other + "/")),
  );
}

function CountBadge({ count, className }: { count: number; className?: string }) {
  if (count <= 0) return null;
  return (
    <span
      className={cn(
        "flex h-5 min-w-5 items-center justify-center rounded-full bg-[#ff8d6e] px-1.5 text-[10px] font-bold text-[#003a43]",
        className,
      )}
    >
      {count > 99 ? "99+" : count}
    </span>
  );
}

/** Nav sections for this login, split into top-nav items and the rest (side rail). */
function usePracticeNav(user: ReturnType<typeof useAuth>["user"]) {
  const recordExchangePending = useRecordExchangePendingCount();
  const messageUnread = useUnreadCount();
  const messageUnreadCount = typeof messageUnread.data === "number" ? messageUnread.data : 0;
  const pendingBookingRequestsCount = usePendingBookingRequestsCount();

  const { can } = usePermissions();
  const assignedPrp = isPrpAssigned(user);
  const canUsePrp = can("prp", "list") || assignedPrp;
  const navSections = useMemo(() => {
    const canFn = (resource: string, action: string) =>
      can(resource, action) || (resource === "prp" && action === "list" && assignedPrp);
    return withProgramSections(getNavSections(user?.role, canFn), canUsePrp)
      .map((section) => ({ ...section, items: section.items.filter((item) => !isHiddenHref(item.href)) }))
      .filter((section) => section.items.length > 0);
  }, [user?.role, can, canUsePrp, assignedPrp]);

  const allItems = useMemo(() => navSections.flatMap((section) => section.items), [navSections]);
  const allHrefs = useMemo(() => allItems.map((item) => item.href), [allItems]);

  const primaryItems = useMemo(() => {
    const ranked = PRIMARY_NAV_HREFS.map((href) => allItems.find((item) => item.href === href)).filter(
      (item): item is PracticeNavItem => Boolean(item),
    );
    return ranked.slice(0, MAX_PRIMARY_ITEMS);
  }, [allItems]);

  const moreSections = useMemo(() => {
    const primary = new Set(primaryItems.map((item) => item.href));
    return navSections
      .map((section) => ({ ...section, items: section.items.filter((item) => !primary.has(item.href)) }))
      .filter((section) => section.items.length > 0);
  }, [navSections, primaryItems]);

  const badgeFor = (href: string) => {
    if (href === "/messages") return messageUnreadCount;
    if (href === "/record-exchange") return recordExchangePending;
    if (href === "/schedule/booking-requests") return pendingBookingRequestsCount;
    return 0;
  };

  return { navSections, allHrefs, primaryItems, moreSections, badgeFor };
}

// ── Hover-expand controls ─────────────────────────────────────────────────────

/** Classes that make a shared header button (bell, theme…) sit flat inside a HoverExpand. */
const EXPAND_CHILD_CLASS =
  "h-10 w-10 rounded-full bg-transparent text-inherit ring-0 hover:bg-transparent hover:text-inherit";

/**
 * Wraps one round icon control; on hover/focus the pill turns teal and slides
 * the label out to the right. Clicking the label clicks the wrapped control.
 */
function HoverExpand({
  label,
  tone = "default",
  className,
  children,
}: {
  label: string;
  tone?: "default" | "active" | "amber";
  className?: string;
  children: ReactNode;
}) {
  const [hovered, setHovered] = useState(false);
  const ref = useRef<HTMLDivElement>(null);
  return (
    <div
      ref={ref}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      onFocusCapture={() => setHovered(true)}
      onBlurCapture={() => setHovered(false)}
      className={cn(
        "flex h-10 shrink-0 items-center rounded-full transition-colors",
        hovered
          ? "bg-[#003a43] text-white"
          : tone === "active"
            ? "bg-muted text-[#003a43] dark:text-foreground"
            : tone === "amber"
              ? "text-[#c4532f]"
              : "text-muted-foreground",
        className,
      )}
    >
      {children}
      <AnimatePresence initial={false}>
        {hovered && (
          <motion.span
            initial={{ width: 0, opacity: 0 }}
            animate={{ width: "auto", opacity: 1 }}
            exit={{ width: 0, opacity: 0 }}
            transition={{ duration: 0.2, ease: "easeOut" }}
            onClick={() => ref.current?.querySelector("button")?.click()}
            className="cursor-pointer overflow-hidden whitespace-nowrap text-sm font-medium"
          >
            <span className="block pr-4">{label}</span>
          </motion.span>
        )}
      </AnimatePresence>
    </div>
  );
}

function ExpandIconButton({
  icon: Icon,
  label,
  onClick,
  tone,
  className,
}: {
  icon: ElementType;
  label: string;
  onClick: () => void;
  tone?: "default" | "active" | "amber";
  className?: string;
}) {
  return (
    <HoverExpand label={label} tone={tone} className={className}>
      <button type="button" aria-label={label} onClick={onClick} className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full">
        <Icon className="h-[18px] w-[18px]" />
      </button>
    </HoverExpand>
  );
}

/** Rail icon; on hover it grows into a pill with the label, floating over the page. */
function RailLink({
  icon: Icon,
  label,
  href,
  active,
  badge = 0,
  onClick,
}: {
  icon: ElementType;
  label: string;
  href?: string;
  active?: boolean;
  badge?: number;
  onClick?: () => void;
}) {
  const [hovered, setHovered] = useState(false);
  const body = (
    <>
      <Icon className="h-[18px] w-[18px] shrink-0" />
      {badge > 0 && (
        <span className="absolute right-1.5 top-1.5 h-2 w-2 rounded-full bg-[#ff8d6e] ring-2 ring-card" aria-hidden />
      )}
      <AnimatePresence>
        {hovered && (
          <motion.span
            initial={{ width: 40, opacity: 0 }}
            animate={{ width: "auto", opacity: 1 }}
            exit={{ width: 40, opacity: 0 }}
            transition={{ duration: 0.2, ease: "easeOut" }}
            className="pointer-events-none absolute left-0 top-0 z-50 flex h-10 items-center overflow-hidden whitespace-nowrap rounded-full bg-[#003a43] pl-[11px] pr-4 text-sm font-medium text-white shadow-lg shadow-[#003a43]/20"
          >
            <Icon className="h-[18px] w-[18px] shrink-0 text-[#ff8d6e]" />
            <span className="pl-2.5">{label}</span>
            {badge > 0 && (
              <span className="ml-2 rounded-full bg-[#ff8d6e] px-1.5 text-[10px] font-bold text-[#003a43]">
                {badge > 99 ? "99+" : badge}
              </span>
            )}
          </motion.span>
        )}
      </AnimatePresence>
    </>
  );
  const shared = {
    "aria-label": label,
    onMouseEnter: () => setHovered(true),
    onMouseLeave: () => setHovered(false),
    onFocus: () => setHovered(true),
    onBlur: () => setHovered(false),
    className: cn(
      "relative flex h-10 w-10 shrink-0 items-center justify-center rounded-full transition-colors",
      active ? "bg-[#003a43] text-white dark:bg-primary dark:text-primary-foreground" : "text-muted-foreground hover:text-foreground",
    ),
  };
  return href ? (
    <Link to={href} onClick={onClick} {...shared}>
      {body}
    </Link>
  ) : (
    <button type="button" onClick={onClick} {...shared}>
      {body}
    </button>
  );
}

const RAIL_PILL_CLASS = "flex flex-col items-center gap-0.5 rounded-full bg-card p-1 shadow-[0_1px_2px_rgba(0,58,67,0.05)]";

/** Slim floating rail with the pages that don't fit in the top nav. */
function NavRail({ nav, onLogout }: { nav: ReturnType<typeof usePracticeNav>; onLogout: () => void }) {
  const location = useLocation();
  return (
    <aside className="z-30 hidden w-12 shrink-0 flex-col items-center justify-between gap-2 lg:flex" aria-label="More pages">
      <div className={RAIL_PILL_CLASS}>
        {nav.moreSections.map((section, index) => (
          <div key={section.label} className="flex flex-col items-center gap-0.5">
            {index > 0 && <span className="my-0.5 h-px w-5 bg-border" aria-hidden />}
            {section.items.map((item) => (
              <RailLink
                key={item.href}
                icon={item.icon}
                label={item.label}
                href={item.href}
                active={isNavActive(location.pathname, item.href, nav.allHrefs)}
                badge={nav.badgeFor(item.href)}
              />
            ))}
          </div>
        ))}
      </div>
      <div className={RAIL_PILL_CLASS}>
        <RailLink icon={CircleHelp} label="Help" href="/help" active={location.pathname === "/help"} />
        <RailLink icon={LogOut} label="Sign out" onClick={onLogout} />
      </div>
    </aside>
  );
}

interface FloatingHeaderProps {
  user: ReturnType<typeof useAuth>["user"];
  /** Opens global search; without it the search button goes to Patients. */
  onSearch?: () => void;
}

/** Shared with SectionShell so Account and Settings keep the same top bar. */
export function FloatingHeader({ user, onSearch }: FloatingHeaderProps) {
  const workbench = useWorkbench();
  const location = useLocation();
  const navigate = useNavigate();
  const nav = usePracticeNav(user);
  const { navSections, allHrefs, primaryItems, badgeFor } = nav;

  const menuSections = (sections: typeof navSections) =>
    sections.map((section, index) => (
      <div key={section.label}>
        {index > 0 && <DropdownMenuSeparator />}
        <DropdownMenuLabel className="px-3 pt-2 text-[11px] font-semibold uppercase tracking-[0.14em] text-muted-foreground">
          {section.label}
        </DropdownMenuLabel>
        {section.items.map((item) => {
          const active = isNavActive(location.pathname, item.href, allHrefs);
          return (
            <DropdownMenuItem
              key={item.href}
              onClick={() => navigate(item.href)}
              className={cn(
                "h-10 cursor-pointer gap-3 rounded-xl px-3 text-sm font-medium",
                active && "bg-muted text-foreground",
              )}
            >
              <item.icon className={cn("h-4 w-4", active ? "text-[#c4532f]" : "text-muted-foreground")} />
              <span className="flex-1">{item.label}</span>
              <CountBadge count={badgeFor(item.href)} />
            </DropdownMenuItem>
          );
        })}
      </div>
    ));

  return (
    <header className="z-40 flex shrink-0 items-center gap-2 px-3 py-2.5 sm:px-4">
      {/* Brand */}
      <Link to="/dashboard" className={cn(PILL_CLASS, "gap-2 pl-1.5 pr-1.5 xl:pr-4")} aria-label="Orange home">
        <span className="flex h-9 w-9 items-center justify-center rounded-full bg-[#f8f3eb] dark:bg-muted">
          <img src="/logo/logo.png" alt="" className="h-6 w-6" />
        </span>
        <BrandLogotype className="hidden text-lg xl:inline" />
      </Link>

      {/* Main nav (desktop) */}
      <nav className={cn(PILL_CLASS, "hidden min-w-0 gap-0.5 px-1.5 lg:flex")} aria-label="Main">
        {primaryItems.map((item) => {
          const active = isNavActive(location.pathname, item.href, allHrefs);
          return (
            <Link
              key={item.href}
              to={item.href}
              className={cn(
                "flex h-9 items-center gap-2 whitespace-nowrap rounded-full px-3.5 text-sm font-medium transition-colors",
                active
                  ? "bg-[#003a43] text-white dark:bg-primary dark:text-primary-foreground"
                  : "text-foreground/70 hover:bg-muted hover:text-foreground",
              )}
            >
              {item.label}
              <CountBadge count={badgeFor(item.href)} />
            </Link>
          );
        })}
      </nav>

      {/* Main nav (mobile / tablet) */}
      <DropdownMenu>
        <DropdownMenuTrigger className={cn(PILL_CLASS, "gap-2 px-5 text-sm font-medium text-foreground outline-none lg:hidden")}>
          <Menu className="h-5 w-5" />
          <span className="hidden sm:inline">Menu</span>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="start" className="mt-2 max-h-[70vh] w-64 overflow-y-auto rounded-2xl p-1.5">
          {menuSections(navSections)}
          <DropdownMenuSeparator />
          <DropdownMenuItem onClick={() => navigate("/help")} className="h-10 cursor-pointer gap-3 rounded-xl px-3 text-sm font-medium">
            <CircleHelp className="h-4 w-4 text-muted-foreground" />
            Help
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>

      <div className="ml-auto flex min-w-0 items-center gap-2">
        {/* Tools */}
        <div
          className={cn(PILL_CLASS, "gap-0 px-1")}
        >
          <ExpandIconButton icon={Search} label="Search" onClick={onSearch ?? (() => navigate("/patients"))} />
          <ExpandIconButton icon={Plus} label="Book appointment" className="hidden sm:flex" onClick={() => navigate("/schedule/book")} />
          <ExpandIconButton icon={Calendar} label="Schedule" className="hidden sm:flex" onClick={() => navigate("/schedule")} />
          <HoverExpand label="Notifications">
            <NotificationBell className={EXPAND_CHILD_CLASS} />
          </HoverExpand>
          <HoverExpand label="Theme" className="hidden md:flex">
            <ThemePicker variant="icon" className={EXPAND_CHILD_CLASS} />
          </HoverExpand>
          <ExpandIconButton
            icon={Rows3}
            label={workbench.showTabs ? "Hide tab bar" : "Show tab bar"}
            tone={workbench.showTabs ? "active" : "default"}
            className="hidden md:flex"
            onClick={workbench.toggleTabs}
          />
          <ExpandIconButton
            icon={Sparkles}
            label={workbench.copilotEnabled ? "AI Copilot on" : "AI Copilot"}
            tone={workbench.copilotEnabled ? "amber" : "default"}
            className="hidden md:flex"
            onClick={workbench.toggleCopilot}
          />
        </div>

        <UserMenu className="h-12 shrink-0 rounded-full border-0 bg-card pl-1.5 pr-3 shadow-[0_1px_2px_rgba(0,58,67,0.05)] hover:bg-card sm:pl-1.5 sm:pr-3.5" />
      </div>
    </header>
  );
}

// ── Main Layout ───────────────────────────────────────────────────────────────

function PracticeLayoutInner() {
  const [searchOpen, setSearchOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [bookingPatient, setBookingPatient] = useState<Patient | null>(null);
  const [recentSearches, setRecentSearches] = useState<SearchResultItem[]>([]);

  const { user, logout } = useAuth();
  const nav = usePracticeNav(user);
  const location = useLocation();
  const navigate = useNavigate();
  const isMessagesWorkspace =
    location.pathname === "/messages" || location.pathname.startsWith("/messages/");
  const debouncedQuery = useDebounce(searchQuery.trim(), 350);

  useEffect(() => {
    const handler = (event: KeyboardEvent) => {
      if ((event.metaKey || event.ctrlKey) && event.key.toLowerCase() === "k") {
        event.preventDefault();
        setSearchOpen(true);
      }
    };

    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
  }, []);

  // (Messages dropdown is handled inside the collapsed sidebar hover UI.)

  useEffect(() => {
    try {
      if (!user?.id) {
        setRecentSearches([]);
        return;
      }
      const hydrated = readRecentSearches(user.id)
        .map((entry): SearchResultItem => ({
          ...entry,
          icon: entry.kind === "patient" ? UserRoundSearch : entry.kind === "appointment" ? Calendar : Search,
        }));
      setRecentSearches(hydrated.slice(0, 5));
    } catch {
      setRecentSearches([]);
    }
  }, [user?.id]);

  const filteredSearchTargets = useMemo(() => {
    const term = searchQuery.trim().toLowerCase();
    if (!term) return SEARCH_TARGETS;

    return SEARCH_TARGETS.filter((target) => {
      const haystack = `${target.label} ${target.subtitle} ${target.keywords.join(" ")}`.toLowerCase();
      return haystack.includes(term);
    });
  }, [searchQuery]);

  const suggestionTargets = useMemo(() => {
    if (searchQuery.trim()) return filteredSearchTargets.slice(0, 6);
    const defaults = ["search-patients", "search-schedule", "search-orders", "search-billing"];
    return defaults
      .map((id) => SEARCH_TARGETS.find((target) => target.id === id))
      .filter((target): target is SearchTarget => Boolean(target));
  }, [filteredSearchTargets, searchQuery]);

  const {
    data: patientMatches,
    isFetching: isPatientsFetching,
  } = useSearchPatients(debouncedQuery);

  const patientSearchIds = useMemo(
    () => (patientMatches ?? []).slice(0, 6).map((p: Patient) => p.id),
    [patientMatches],
  );
  const { data: announcementSummaries = {} } = usePatientAnnouncementSummaries(patientSearchIds);

  const {
    data: appointmentsResponse,
    isFetching: isAppointmentsFetching,
  } = useQuery({
    queryKey: ["global-search", "appointments", debouncedQuery],
    queryFn: () => listAppointments({ search: debouncedQuery, page: 1, per_page: 6 }),
    enabled: debouncedQuery.length >= 3,
    staleTime: 30_000,
  });

  const patientResults = useMemo<SearchResultItem[]>(() => {
    const rows = (patientMatches ?? []).slice(0, 6);
    return rows.map((patient: Patient) => ({
      id: `patient-${patient.id}`,
      patientId: patient.id,
      label: `${patient.first_name} ${patient.last_name}`.trim() || patient.mrn,
      subtitle: [patient.mrn ? `MRN ${patient.mrn}` : null, patient.date_of_birth || null]
        .filter(Boolean)
        .join(" · "),
      href: `/patients/${patient.id}`,
      icon: UserRoundSearch,
    }));
  }, [patientMatches]);

  const appointmentResults = useMemo<SearchResultItem[]>(() => {
    const rows = (appointmentsResponse?.data ?? []).slice(0, 6);
    return rows.map((appointment: Appointment) => {
      const startsAt = new Date(appointment.start_time);
      const dateLabel = Number.isNaN(startsAt.getTime())
        ? appointment.start_time
        : startsAt.toLocaleString([], {
            month: "short",
            day: "numeric",
            hour: "numeric",
            minute: "2-digit",
          });

      return {
        id: `appointment-${appointment.id}`,
        label: appointment.patient_name || "Appointment",
        subtitle: `${dateLabel} · ${appointment.status.replace(/_/g, " ")}`,
        href: "/schedule",
        icon: Calendar,
      };
    });
  }, [appointmentsResponse]);

  const quickLinkResults = useMemo(() => {
    if (!searchQuery.trim()) {
      return suggestionTargets.slice(0, 6);
    }
    return filteredSearchTargets.slice(0, 6);
  }, [filteredSearchTargets, searchQuery, suggestionTargets]);

  const isApiSearching = debouncedQuery.length >= 3 && (isPatientsFetching || isAppointmentsFetching);
  const hasApiResults = patientResults.length > 0 || appointmentResults.length > 0;

  const rememberSearch = (
    target: SearchResultItem,
    kind: StoredRecentSearch["kind"] = "navigation",
  ) => {
    if (!user?.id) return;
    setRecentSearches((prev) => {
      const updated = [target, ...prev.filter((item) => item.id !== target.id)].slice(0, 5);
      writeRecentSearches(
        user.id,
        updated.map((item): StoredRecentSearch => ({
          id: item.id,
          label: item.label,
          subtitle: item.subtitle,
          href: item.href,
          patientId: item.patientId,
          kind: item.patientId ? "patient" : kind,
        })),
      );
      return updated;
    });
  };

  const openSearchResult = (target: SearchTarget) => {
    setSearchOpen(false);
    setSearchQuery("");
    navigate(target.href);
    rememberSearch(target);
  };

  const openDynamicResult = (result: SearchResultItem) => {
    setSearchOpen(false);
    setSearchQuery("");
    navigate(result.href);
    rememberSearch(
      result,
      result.patientId
        ? "patient"
        : result.id.startsWith("appointment-")
          ? "appointment"
          : "navigation",
    );
  };

  const bookPatientAppointment = (result: SearchResultItem) => {
    const patient = (patientMatches ?? []).find(
      (match: Patient) => match.id === result.patientId,
    );
    if (!patient) return;

    setSearchOpen(false);
    setSearchQuery("");
    rememberSearch(result, "patient");
    setBookingPatient(patient);
  };

  const continuePatientBooking = (patient: Patient) => {
    setBookingPatient(null);
    navigate("/schedule/book", {
      state: { selectedPatient: patient },
    });
  };

  const registerPatientFromSearch = () => {
    const nameParts = searchQuery.trim().split(/\s+/).filter(Boolean);
    const firstName = nameParts[0] ?? "";
    const lastName = nameParts.slice(1).join(" ");
    const returnTo = location.pathname.startsWith("/schedule")
      ? "/schedule/book"
      : location.pathname;

    setSearchOpen(false);
    setSearchQuery("");
    navigate("/patients/new", {
      state: {
        registrationContext: {
          returnTo,
          intent: "book-appointment",
          prefill: { firstName, lastName },
        },
      },
    });
  };

  return (
    <div className="app-shell relative flex h-screen flex-col overflow-hidden bg-[var(--canvas)]">
      <Dialog open={searchOpen} onOpenChange={setSearchOpen}>
        <DialogContent className="max-w-3xl p-0 gap-0 overflow-hidden rounded-3xl glass-dialog border-primary/20 shadow-2xl">
          <DialogHeader className="px-6 pt-5 pb-4 border-b border-border/40 bg-background/20 backdrop-blur-md">
            <DialogTitle className="flex items-center gap-2 text-lg font-black text-foreground">
              <Search className="h-5 w-5 text-primary" />
              Search
            </DialogTitle>
          </DialogHeader>

          <div className="p-6 bg-primary/5 border-b border-border/40 relative">
            <div className="absolute inset-0 bg-gradient-to-br from-primary/5 via-transparent to-transparent pointer-events-none" />
            <div className="relative">
              <Search className="absolute left-4 top-1/2 h-4 w-4 -translate-y-1/2 text-primary/40" />
              <input
                autoFocus
                type="text"
                value={searchQuery}
                onChange={(event) => setSearchQuery(event.target.value)}
                placeholder="Search clients, charts, appointments..."
                className="h-12 w-full rounded-2xl border-none glass-input pl-11 pr-12 text-sm font-medium text-foreground outline-none ring-primary/20 transition focus:ring-4"
              />
              <span className="absolute right-3 top-1/2 -translate-y-1/2 rounded-lg border border-border bg-background/50 px-2.5 py-1 text-[10px] font-black uppercase tracking-widest text-muted-foreground backdrop-blur-sm">
                ESC
              </span>
            </div>
          </div>

          <div className="grid gap-0 md:grid-cols-[1.2fr_1fr] bg-background/20 backdrop-blur-md">
            <div className="border-r border-border/40 p-4 max-h-[420px] overflow-y-auto">
              <p className="px-2 pb-2 text-[10px] font-black uppercase tracking-[0.2em] text-primary/40">
                Suggestions
              </p>
              <div className="space-y-1">
                {searchQuery.trim().length > 0 && searchQuery.trim().length < 2 && (
                  <p className="px-2 py-4 text-xs font-medium text-slate-500">
                    Keep typing to search records. Quick links are shown below.
                  </p>
                )}

                {isApiSearching && (
                  <p className="px-2 py-4 text-xs font-medium text-slate-500">
                    Searching patients and appointments...
                  </p>
                )}

                {patientResults.length > 0 && (
                  <p className="px-2 pt-1 pb-1 text-[10px] font-black uppercase tracking-[0.2em] text-slate-400">
                    Patients
                  </p>
                )}
                {patientResults.map((result) => {
                  const Icon = result.icon;
                  return (
                    <div
                      key={result.id}
                      className="flex w-full items-center gap-3 rounded-2xl px-3 py-2.5 transition hover:bg-primary/5 group"
                    >
                      <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-card border border-border shadow-glass-sm text-primary group-hover:scale-110 transition-transform">
                        <Icon className="h-4 w-4" />
                      </span>
                      <span className="min-w-0 flex-1">
                        <span className="flex items-center gap-2">
                          <span className="block truncate text-sm font-black text-foreground">{result.label}</span>
                          {result.patientId && (
                            <PatientAnnouncementIndicator
                              summary={announcementSummaries[result.patientId]}
                              size="md"
                            />
                          )}
                        </span>
                        <span className="block truncate text-[11px] font-bold text-muted-foreground uppercase tracking-widest">{result.subtitle}</span>
                      </span>
                      <span className="flex shrink-0 items-center gap-1.5">
                        <Button
                          type="button"
                          size="sm"
                          variant="ghost"
                          className="h-8 rounded-lg px-2.5 text-xs font-bold"
                          onClick={() => openDynamicResult(result)}
                        >
                          Open chart
                        </Button>
                        <Button
                          type="button"
                          size="sm"
                          className="h-8 gap-1.5 rounded-lg px-2.5 text-xs font-bold"
                          onClick={() => bookPatientAppointment(result)}
                        >
                          <Calendar className="h-3.5 w-3.5" />
                          Book
                        </Button>
                      </span>
                    </div>
                  );
                })}

                {debouncedQuery.length >= 3 &&
                  !isPatientsFetching &&
                  patientResults.length === 0 && (
                    <div className="mx-2 my-2 rounded-2xl border border-dashed border-primary/30 bg-primary/5 p-3">
                      <p className="text-sm font-black text-foreground">Patient not found</p>
                      <p className="mt-1 text-xs font-medium text-muted-foreground">
                        Register this patient, then return directly to appointment booking.
                      </p>
                      <Button
                        type="button"
                        size="sm"
                        className="mt-3 w-full gap-2 rounded-xl"
                        onClick={registerPatientFromSearch}
                      >
                        <UserPlus className="h-4 w-4" />
                        Register patient &amp; book appointment
                      </Button>
                    </div>
                  )}

                {appointmentResults.length > 0 && (
                  <p className="px-2 pt-2 pb-1 text-[10px] font-black uppercase tracking-[0.2em] text-primary/40">
                    Appointments
                  </p>
                )}
                {appointmentResults.map((result) => {
                  const Icon = result.icon;
                  return (
                    <button
                      type="button"
                      key={result.id}
                      onClick={() => openDynamicResult(result)}
                      className="flex w-full items-center gap-3 rounded-2xl px-3 py-2.5 text-left transition hover:bg-primary/10 active:scale-[0.98] group"
                    >
                      <span className="flex h-10 w-10 items-center justify-center rounded-xl bg-card border border-border shadow-glass-sm text-primary group-hover:scale-110 transition-transform">
                        <Icon className="h-4 w-4" />
                      </span>
                      <span className="min-w-0 flex-1">
                        <span className="block truncate text-sm font-black text-foreground">{result.label}</span>
                        <span className="block truncate text-[11px] font-bold text-muted-foreground uppercase tracking-widest">{result.subtitle}</span>
                      </span>
                    </button>
                  );
                })}

                {quickLinkResults.length > 0 && (
                  <p className="px-2 pt-2 pb-1 text-[10px] font-black uppercase tracking-[0.2em] text-primary/40">
                    Quick Navigation
                  </p>
                )}
                {quickLinkResults.map((target) => {
                  const Icon = target.icon;
                  return (
                    <button
                      type="button"
                      key={target.id}
                      onClick={() => openSearchResult(target)}
                      className="flex w-full items-center gap-3 rounded-2xl px-3 py-2.5 text-left transition hover:bg-primary/10 active:scale-[0.98] group"
                    >
                      <span className="flex h-10 w-10 items-center justify-center rounded-xl bg-card border border-border shadow-glass-sm text-primary group-hover:scale-110 transition-transform">
                        <Icon className="h-4 w-4" />
                      </span>
                      <span className="min-w-0 flex-1">
                        <span className="block truncate text-sm font-black text-foreground">{target.label}</span>
                        <span className="block truncate text-[11px] font-bold text-muted-foreground uppercase tracking-widest">{target.subtitle}</span>
                      </span>
                    </button>
                  );
                })}

                {searchQuery.trim() && !isApiSearching && !hasApiResults && quickLinkResults.length === 0 && (
                  <p className="px-2 py-6 text-center text-sm font-medium text-slate-500">
                    No matches. Try "patient", "schedule", or "forms".
                  </p>
                )}
              </div>
            </div>

            <div className="p-4 max-h-[420px] overflow-y-auto bg-card/30">
              <p className="px-2 pb-2 text-[10px] font-black uppercase tracking-[0.2em] text-primary/40">
                Recent Searches
              </p>
              {recentSearches.length === 0 ? (
                <p className="rounded-2xl border border-dashed border-slate-200 bg-slate-50 px-4 py-5 text-xs font-medium text-slate-500 dark:bg-muted/60 dark:border-border">
                  Your recent searches will appear here.
                </p>
              ) : (
                <div className="space-y-1">
                  {recentSearches.map((target) => {
                    const Icon = target.icon;
                    return (
                      <button
                        type="button"
                        key={`recent-${target.id}`}
                        onClick={() => openDynamicResult(target)}
                        className="flex w-full items-center gap-3 rounded-2xl px-3 py-2.5 text-left transition hover:bg-slate-50 dark:hover:bg-muted/60"
                      >
                        <span className="flex h-9 w-9 items-center justify-center rounded-xl bg-slate-50 ring-1 ring-slate-200 text-slate-500 dark:bg-muted/60">
                          <History className="h-4 w-4" />
                        </span>
                        <span className="min-w-0 flex-1">
                          <span className="block truncate text-sm font-semibold text-slate-900 dark:text-foreground">{target.label}</span>
                          <span className="block truncate text-xs font-medium text-slate-500">{target.subtitle}</span>
                        </span>
                        <Icon className="h-4 w-4 text-slate-400" />
                      </button>
                    );
                  })}
                </div>
              )}
            </div>
          </div>
        </DialogContent>
      </Dialog>

      <PatientBookingAlertDialog
        patient={bookingPatient}
        open={!!bookingPatient}
        onOpenChange={(nextOpen) => {
          if (!nextOpen) setBookingPatient(null);
        }}
        onContinue={continuePatientBooking}
        onOpenChart={(patient) => {
          setBookingPatient(null);
          navigate(`/patients/${patient.id}`);
        }}
      />

      <FloatingHeader user={user} onSearch={() => setSearchOpen(true)} />

      <div className="flex min-h-0 flex-1 gap-2 px-3 pb-3 sm:px-4">
        <NavRail nav={nav} onLogout={() => logout()} />

      {/* Content panel */}
      <div className="flex min-h-0 min-w-0 flex-1 flex-col overflow-hidden rounded-[24px] bg-background shadow-[0_8px_30px_-12px_rgba(0,58,67,0.25)] ring-1 ring-[#d6cdbb] dark:ring-border">
        <WorkbenchTabBarSlot />

        {/* ── Page Content ───────────────────────────────────────── */}
        <main
          className={cn(
            "relative min-h-0 flex-1",
            isMessagesWorkspace ? "overflow-hidden" : "overflow-y-auto",
          )}
        >
          
          <div
            className={cn(
              "relative z-10",
              isMessagesWorkspace ? "h-full p-3" : "p-4 pb-16 sm:p-5",
            )}
          >
            <AnimatePresence mode="wait">
              <motion.div
                key={location.pathname}
                className={cn(isMessagesWorkspace && "h-full min-h-0")}
                initial={{ opacity: 0, y: 8 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -8 }}
                transition={{ duration: 0.25, ease: "easeOut" }}
              >
                <Outlet />
              </motion.div>
            </AnimatePresence>
          </div>
        </main>

        <WorkbenchFloatingDock />

      </div>
      </div>
    </div>
  );
}

/**
 * PRP mode (PRP Programs plan, or a PRP staff login): the PRP workspace
 * shell is the whole app, so shared pages (schedule, groups, billing…) render
 * inside it with the PRP sidebar instead of the clinical sidebar.
 */
export function PracticeLayout() {
  const { prpMode, isLoading } = usePrpMode();
  if (isLoading) return null;
  return prpMode ? <PrpWorkspaceLayout /> : <PracticeLayoutInner />;
}

export default PracticeLayout;
